// @ts-strict-ignore
import { Component, inject, signal, WritableSignal } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { map, Observable, tap } from 'rxjs';
import { AlertButton } from '@shared/alert/alertButton';
import { AlertHelper } from '@shared/alert/alert.helper';
import { MdmGroupVO, UserRoleName, userRoleNames, UserVO } from '../../model/vo/user.vo';
import { UserRightsInfoVO } from '@article/model/vo/user-rights-info.vo';
import { UserDataService } from '../../service/user-data.service';
import { ArrayUtils } from '@shared/util/array-utils';
import { toSignal } from '@angular/core/rxjs-interop';

interface UserRightOption {
  userRight: string;
  description: string | undefined;
  isSelected: boolean;
}

@Component({
  standalone: false,
  selector: 'app-user-rights-management',
  templateUrl: './user-rights-management.component.html',
})
export class UserRightsManagementComponent {

  public searchUsername: string = '';
  public userRightsInfo: UserRightsInfoVO;
  public additionalUserInfo: {salesOrganization: string, addressNumber: string, role: string} | undefined;
  public isUnsavedChangesExist: boolean = false;
  public isChangeRightsDisabled: boolean = true;
  private _selectedUserRole: UserRoleName = null;
  private _selectedMdmGroup: MdmGroupVO = null;

  private _bsModalRef: BsModalRef = inject(BsModalRef);
  private userDataService = inject(UserDataService);
  private alertHelper: AlertHelper = inject(AlertHelper);

  public userRightOptions: WritableSignal<UserRightOption[]> = signal([]);
  public allMdmGroups = toSignal(this.userDataService.getAllMdmGroups());

  public constructor() {
    this.userDataService.getVisibleUserRightDefs()
      .pipe(map(userRightDefVOs => userRightDefVOs.map(userRightDef => <UserRightOption>{
          userRight: userRightDef.name,
          description: userRightDef.description,
          isSelected: false
      })))
      .subscribe(userRightOptions => this.userRightOptions.set(userRightOptions));
  }

  public set selectedUserRole(userRole: UserRoleName) {
    const shouldUpdateUserRightOptions: boolean = this._selectedUserRole === 'ADMIN' || userRole === 'ADMIN';
    this._selectedUserRole = userRole;
    if (shouldUpdateUserRightOptions) {
      this.updateUserRightsOptions();
    }
    this.setIsUnsavedChangesExist();
  }

  public get selectedUserRole(): string {
    return this._selectedUserRole;
  }

  public set selectedMdmGroup(mdmGroup: MdmGroupVO) {
    this._selectedMdmGroup = mdmGroup;
    this.setIsUnsavedChangesExist();
  }

  public get selectedMdmGroup(): MdmGroupVO {
    return this._selectedMdmGroup;
  }

  public compareMdmGroups = (a: MdmGroupVO, b: MdmGroupVO): boolean => {
    return MdmGroupVO.areEqual(a, b);
  };

  public async searchUser(): Promise<void> {
    if (this.searchUsername.length < 1) {
      return;
    }
    if (this.isUnsavedChangesExist) {
      await this.showChangesNotSavedAlert(() => this.executeSearchUser());
    } else {
      this.executeSearchUser();
    }
  }

  private executeSearchUser(): void {
    this.userDataService.findUserRightsInfoByUsername(this.searchUsername).subscribe((userRightsInfo: UserRightsInfoVO) => {
      this.isUnsavedChangesExist = false;
      if (userRightsInfo) {
        this.readAdditionalUserInformation();
        this.setUserRightsInfo(userRightsInfo);
      } else {
        this.handleUserNotFoundError();
      }
    });
  }

  private readAdditionalUserInformation() {
    this.userDataService.getAdditionalUserInformation(this.searchUsername).subscribe((userVo: UserVO) => {
      if (userVo) {
        this.additionalUserInfo = {
          salesOrganization: userVo.salesOrganisation,
          addressNumber: userVo.addressNumber,
          role: userVo.cdcUserRole?.name,
        }
      }
    });
  }

  private handleUserNotFoundError(): void {
    this.additionalUserInfo = undefined;
    this.userRightsInfo = null;
    this._selectedUserRole = null;
    this.isChangeRightsDisabled = true;
    this.updateUserRightsOptions();
    this.alertHelper.showError(
      'userRightsManagementDialog.title',
      'userRightsManagementDialog.massage.user_not_found'
    );
  }

  private setUserRightsInfo(userRightsInfo: UserRightsInfoVO): void {
    this.userRightsInfo = userRightsInfo;
    this._selectedUserRole = userRightsInfo.role;
    this._selectedMdmGroup = userRightsInfo.mdmGroup;
    this.updateUserRightsOptions();
  }

  private updateUserRightsOptions(): void {
    this.userRightOptions.update(options => {
      if (!this.userRightsInfo) {
        options.forEach((option: UserRightOption) => option.isSelected = false);
        return options;
      }
      if (this._selectedUserRole === 'ADMIN') {
        options.forEach((option: UserRightOption) => option.isSelected = true);
        this.isChangeRightsDisabled = true;
        return options;
      }
      options.forEach((option: UserRightOption) => {
        option.isSelected = !!this.userRightsInfo.userRights.find((userRight: string) => userRight === option.userRight);
      });
      return options;
    });
    this.isChangeRightsDisabled = false;
  }

  public toggleUserRight(userRightOption: UserRightOption): void {
    if (this.isChangeRightsDisabled) {
      return;
    }
    userRightOption.isSelected = !userRightOption.isSelected;
    this.setIsUnsavedChangesExist();
  }

  public setIsUnsavedChangesExist(): void {
    if (!this.userRightsInfo) {
      this.isUnsavedChangesExist = false;
      return;
    }

    const isMdmGroupChanged: boolean = !this.compareMdmGroups(this.userRightsInfo.mdmGroup, this._selectedMdmGroup);
    const isRoleChanged: boolean = this._selectedUserRole && this._selectedUserRole !== this.userRightsInfo.role;
    const isUserRightsChanged: boolean = !ArrayUtils.bothArraysContainTheSameElements(
      this.userRightsInfo.userRights,
      this.userRightOptions().filter((option: UserRightOption) => option.isSelected)
        .map((option: UserRightOption) => option.userRight)
    );
    this.isUnsavedChangesExist = isMdmGroupChanged || isRoleChanged || isUserRightsChanged;
  }

  public onConfirm(): void {
    if (!this.userRightsInfo) {
      return;
    }
    this.saveChanges().subscribe();
  }

  private saveChanges(): Observable<void> {
    this.userRightsInfo.role = this._selectedUserRole;
    this.userRightsInfo.userRights = this.userRightOptions().filter(option => option.isSelected).map(option => option.userRight);
    this.userRightsInfo.mdmGroup = this.selectedMdmGroup;
    return this.userDataService.updateUserRightsAndRole(this.userRightsInfo).pipe(
      tap(() => this.isUnsavedChangesExist = false));
  }

  public async onCancel(): Promise<void> {
    if (this.isUnsavedChangesExist) {
      await this.showChangesNotSavedAlert(() => this.close());
    } else {
      this.close();
    }
  }

  public close(): void {
    this._bsModalRef.hide();
  }

  private async showChangesNotSavedAlert(callback: Function): Promise<void> {
    const answer = await this.alertHelper.showAlert('userRightsManagementDialog.title', 'userRightsManagementDialog.massage.apply_not_saved_changes',
      null, [AlertButton.YES, AlertButton.NO, AlertButton.CANCEL], "error");
    if (answer === AlertButton.YES) {
      this.saveChanges().subscribe(() => callback());
    } else if (answer === AlertButton.NO) {
      callback();
    }
  }

  protected readonly userRoleNames = userRoleNames;
}
